import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";

import type { ISwSettings } from "app/@types/ISwSettings";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { NoPrintContainer } from "app/components/layout/NoPrintContainer";
import { openHooksModalV5 } from "app/services/ModalService";
import { PdfExportService } from "app/services/PdfExportService";
import { getPDFCallback } from "app/UtilitiesAndConstants/UtilityFunctions/getPDF";
import { useSelectedIndustry } from "app/pages/digital-marketing/ad-intelligence/shared/components/AssetPicker/useSelectedIndustry";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const PUBLISHER_ANALYSIS_HOOK_KEY = "PublisherAnalysisUpgradeToTouch";
const ADVERTISER_ACTIVITY_HOOK_KEY = "AdvertiserActivityUpgradeToTouch";

const stateToHookKeyMap = {
    publisheranalysis: PUBLISHER_ANALYSIS_HOOK_KEY,
    displayadvertising: ADVERTISER_ACTIVITY_HOOK_KEY,
};

interface IAdIntPdfDownloadButtonProps {
    setIsPdfLoading: (value: boolean) => void;
    pdfTitleString?: string;
}

export const PdfDownloadButton = ({ setIsPdfLoading, pdfTitleString }: IAdIntPdfDownloadButtonProps) => {
    const { translate } = useTranslate();
    const { industry } = useSelectedIndustry();
    const currentState = proNavigator.current();
    const hookKey = Object.entries(stateToHookKeyMap).find(([key]) => currentState?.name?.includes(key))?.[1] ?? "";
    const pdfAvailability = (swSettings as ISwSettings).components.AdIntDisplay?.resources
        ?.IsExportToPDFAllowed as EAvailabilityMode;
    const isAdIntPdfDownloadHook = pdfAvailability === EAvailabilityMode.HOOK;
    const isAdIntPdfDownloadHidden = pdfAvailability === EAvailabilityMode.HIDDEN;

    const pdfDownloadsMethod = () =>
        PdfExportService.downloadPdfFedService(
            "ad.intelligence.overview.pdf.report.link",
            industry
                ? "ad.intelligence.industry.overview.pdf.report.title"
                : (pdfTitleString ?? "ad.intelligence.advertiser.overview.pdf.report.title"),
            {
                overrideParams: { adsViewType: 1 },
                coverHref: encodeURIComponent(
                    `${swSettings.swsites.pro}/${proNavigator.getStateUrl(currentState, proNavigator.getParams())}`,
                ),
            },
        );

    const onClickDownloadPdf = isAdIntPdfDownloadHook
        ? () => {
              openHooksModalV5(hookKey);
          }
        : getPDFCallback(setIsPdfLoading, pdfDownloadsMethod, false);

    return isAdIntPdfDownloadHidden ? null : (
        <NoPrintContainer>
            <IconButton
                dataAutomation="ad-intelligence-pdf-download-button"
                onClick={onClickDownloadPdf}
                iconName={isAdIntPdfDownloadHook ? "sparkles" : "download"}
                type={isAdIntPdfDownloadHook ? "flatTrial" : "flat"}
            >
                {translate("btn.download.pdf")}
            </IconButton>
        </NoPrintContainer>
    );
};
